import React, { useState, useEffect } from 'react'
import {
  Button,
  Loader,
  Flex,
  Text,
  Heading,
  Toast,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Icon,
} from '@vibe/core'
import { Add, Edit, Delete, Show, Settings, Person } from '@vibe/icons'
import { Layout } from '../components/layout'
import { AuthGuard } from '../components/common/AuthGuard'
import { PermissionGuard } from '../components/common/PermissionGuard'
import { SimpleWorkflowBuilder } from '../components/forms/SimpleWorkflowBuilder'
import WorkflowViewer from '../components/dashboard/WorkflowViewer'
import { usePermissions } from '../hooks/usePermissions'
import { ApiService } from '../services/api'
import type {
  WorkflowConfiguration,
  GetWorkflowsResponse,
  ApiError,
} from '../types'

// Simple error handler
const handleApiError = (error: any): string => {
  return error?.message || 'Ha ocurrido un error'
}

interface WorkflowManagementProps {
  apiService?: ApiService
}

export const WorkflowManagement: React.FC<WorkflowManagementProps> = ({
  apiService = new ApiService(),
}) => {
  const permissions = usePermissions()

  // State management - Single workflow system
  const [workflow, setWorkflow] = useState<WorkflowConfiguration | null>(null)
  const [users, setUsers] = useState<any[]>([]) // Store users for name mapping
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{
    message: string
    type: 'success' | 'error'
  } | null>(null)

  // Modal states
  const [showBuilder, setShowBuilder] = useState(false)
  const [showViewer, setShowViewer] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedWorkflow, setSelectedWorkflow] =
    useState<WorkflowConfiguration | null>(null)

  // Check permissions
  const canManageWorkflows = permissions?.canManageUsers() ?? false // Using user management permission as proxy

  // Load workflows on component mount
  useEffect(() => {
    loadWorkflow()
  }, [])

  /**
   * Load the active workflow and users from API
   */
  const loadWorkflow = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load workflow and users in parallel
      const [workflowResponse, usersResponse] = await Promise.all([
        apiService.getActiveWorkflow(),
        apiService.getUsers(),
      ])

      setWorkflow(workflowResponse)
      setUsers(usersResponse.data.items || [])
    } catch (err) {
      const errorMessage = handleApiError(err)
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle workflow configuration
   */
  const handleConfigureWorkflow = () => {
    setSelectedWorkflow(workflow)
    setShowBuilder(true)
  }

  /**
   * Handle workflow viewing
   */
  const handleViewWorkflow = (workflow: WorkflowConfiguration) => {
    setSelectedWorkflow(workflow)
    setShowViewer(true)
  }

  /**
   * Handle workflow deletion
   */
  const handleDeleteWorkflow = (workflow: WorkflowConfiguration) => {
    setSelectedWorkflow(workflow)
    setShowDeleteConfirm(true)
  }

  /**
   * Confirm workflow deletion
   */
  const confirmDeleteWorkflow = async () => {
    if (!selectedWorkflow) return

    try {
      await apiService.deleteWorkflow(selectedWorkflow.id)
      showToast('Flujo de aprobación desactivado exitosamente', 'success')
      await loadWorkflow() // Reload the workflow
    } catch (err) {
      const errorMessage = handleApiError(err)
      showToast(errorMessage, 'error')
    } finally {
      setShowDeleteConfirm(false)
      setSelectedWorkflow(null)
    }
  }

  /**
   * Handle workflow activation/deactivation
   */
  const handleToggleWorkflowStatus = async (
    workflow: WorkflowConfiguration
  ) => {
    try {
      await apiService.updateWorkflow(workflow.id, {
        is_active: workflow.is_active === 'true' ? 'false' : 'true',
      })
      showToast(
        workflow.is_active === 'true'
          ? 'Flujo desactivado exitosamente'
          : 'Flujo activado exitosamente',
        'success'
      )
      await loadWorkflow() // Reload the workflow
    } catch (err) {
      const errorMessage = handleApiError(err)
      showToast(errorMessage, 'error')
    }
  }

  /**
   * Handle workflow builder submission
   */
  const handleBuilderSubmit = async () => {
    setShowBuilder(false)
    setSelectedWorkflow(null)
    showToast('Flujo de aprobación configurado exitosamente', 'success')
    await loadWorkflow() // Reload the workflow
  }

  /**
   * Handle workflow builder cancellation
   */
  const handleBuilderCancel = () => {
    setShowBuilder(false)
    setSelectedWorkflow(null)
  }

  /**
   * Handle workflow viewer close
   */
  const handleViewerClose = () => {
    setShowViewer(false)
    setSelectedWorkflow(null)
  }

  /**
   * Show toast notification
   */
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }

  /**
   * Get user name by ID
   */
  const getUserName = (userId: string): string => {
    const user = users.find(u => u.id === userId)
    return user ? user.name || user.email : `Usuario ID: ${userId}`
  }

  /**
   * Format date for display
   */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Get workflow status badge
   */
  const getStatusBadge = (workflow: WorkflowConfiguration) => {
    return workflow.is_active === 'true' ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Activo
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Inactivo
      </span>
    )
  }

  return (
    <AuthGuard>
      <PermissionGuard requirePermission={() => canManageWorkflows}>
        <Layout>
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <Flex justify="space-between" align="center">
                <div>
                  <Heading type="h1" className="mb-2">
                    Configuración de Flujo de Aprobación
                  </Heading>
                  <Text type="text3" color="secondary" className="mt-1">
                    Configura quiénes deben aprobar las solicitudes de trabajo
                    antes de que se publiquen
                  </Text>
                </div>
                <Flex gap="medium">
                  <Button
                    onClick={handleConfigureWorkflow}
                    data-testid="configure-workflow-button"
                  >
                    <Icon icon={Settings} iconSize={16} />
                    {workflow
                      ? 'Configurar Aprobadores'
                      : 'Activar Flujo de Aprobación'}
                  </Button>
                  {workflow && (
                    <Button
                      color="negative"
                      kind="secondary"
                      onClick={() => handleDeleteWorkflow(workflow)}
                      data-testid="delete-workflow-button"
                    >
                      <Icon icon={Delete} iconSize={16} />
                      Desactivar Flujo
                    </Button>
                  )}
                </Flex>
              </Flex>
            </div>

            {/* Workflow Status */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              {loading && (
                <div className="text-center py-8">
                  <Loader size="large" />
                  <Text className="mt-4">Cargando configuración...</Text>
                </div>
              )}

              {!loading && workflow && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Heading type="h2" className="mb-2">
                        {workflow.name}
                      </Heading>
                      <Text type="text3" color="secondary">
                        {workflow.description}
                      </Text>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        Activo
                      </span>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <Text weight="medium" className="mb-3">
                      Aprobadores configurados:
                    </Text>
                    {(() => {
                      const logic = workflow.workflow_logic
                      if (!logic) {
                        return (
                          <Text type="text3" color="secondary">
                            No hay aprobadores configurados
                          </Text>
                        )
                      }

                      // Handle single USER type
                      if (logic.type === 'USER' && logic.user_id) {
                        return (
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                              <Icon icon={Person} iconSize={16} />
                              <Text>{getUserName(logic.user_id)}</Text>
                            </div>
                          </div>
                        )
                      }

                      // Handle AND/OR with conditions
                      if (logic.conditions && logic.conditions.length > 0) {
                        return (
                          <div className="space-y-2">
                            {logic.conditions
                              .filter(
                                (condition: any) => condition.type === 'USER'
                              )
                              .map((condition: any, index: number) => (
                                <div
                                  key={
                                    condition.user_id || `condition-${index}`
                                  }
                                  className="flex items-center space-x-2 p-2 bg-gray-50 rounded"
                                >
                                  <Icon icon={Person} iconSize={16} />
                                  <Text>{getUserName(condition.user_id)}</Text>
                                </div>
                              ))}
                          </div>
                        )
                      }

                      return (
                        <Text type="text3" color="secondary">
                          No hay aprobadores configurados
                        </Text>
                      )
                    })()}
                  </div>

                  <div className="border-t pt-4">
                    <Text type="text3" color="secondary">
                      <strong>Funcionamiento:</strong> Con este flujo activo,
                      las solicitudes de trabajo requieren aprobación
                      {(() => {
                        const logic = workflow.workflow_logic
                        if (!logic) return ' antes de ser publicadas'

                        if (logic.type === 'USER') {
                          return ' del usuario configurado'
                        }

                        if (logic.conditions && logic.conditions.length === 1) {
                          return ' del usuario configurado'
                        }

                        return ' de todos los usuarios configurados'
                      })()}{' '}
                      antes de ser publicadas. Sin flujo configurado, las
                      solicitudes se publican automáticamente.
                    </Text>
                  </div>
                </div>
              )}

              {!loading && !workflow && (
                <div className="text-center py-8">
                  <div className="mb-4">
                    <Icon
                      icon={Settings}
                      iconSize={48}
                      className="mx-auto text-gray-400"
                    />
                  </div>
                  <Heading type="h3" className="mb-2">
                    Sin Flujo de Aprobación
                  </Heading>
                  <Text type="text3" color="secondary" className="mb-4">
                    Las solicitudes de trabajo se publican automáticamente sin
                    requerir aprobación
                  </Text>
                  <Button onClick={handleConfigureWorkflow}>
                    Activar Flujo de Aprobación
                  </Button>
                </div>
              )}

              {error && !loading && (
                <div className="text-center py-8">
                  <Text color="negative" className="mb-4">
                    {error}
                  </Text>
                  <Button onClick={loadWorkflow}>Reintentar</Button>
                </div>
              )}
            </div>
          </div>

          {/* Workflow Builder Modal */}
          {showBuilder && (
            <SimpleWorkflowBuilder
              workflow={selectedWorkflow}
              onSubmit={handleBuilderSubmit}
              onCancel={handleBuilderCancel}
              apiService={apiService}
            />
          )}

          {/* Workflow Viewer Modal */}
          {showViewer && selectedWorkflow && (
            <Modal show={true} onClose={handleViewerClose}>
              <ModalHeader>
                <Text weight="bold" style={{ fontSize: '20px' }}>
                  Ver Flujo: {selectedWorkflow.name}
                </Text>
              </ModalHeader>
              <ModalContent>
                <div className="p-4">
                  <Text weight="medium" className="mb-4">
                    Configuración del Flujo
                  </Text>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <Text className="mb-2">
                      <strong>Nombre:</strong> {selectedWorkflow.name}
                    </Text>
                    {selectedWorkflow.description && (
                      <Text className="mb-2">
                        <strong>Descripción:</strong>{' '}
                        {selectedWorkflow.description}
                      </Text>
                    )}
                    <Text className="mb-2">
                      <strong>Estado:</strong>{' '}
                      {selectedWorkflow.is_active === 'true'
                        ? 'Activo'
                        : 'Inactivo'}
                    </Text>
                    <Text className="mb-2">
                      <strong>Tipo de Lógica:</strong>{' '}
                      {selectedWorkflow.workflow_logic.type}
                    </Text>
                    <Text>
                      <strong>Creado:</strong>{' '}
                      {formatDate(selectedWorkflow.created_at)}
                    </Text>
                  </div>
                </div>
              </ModalContent>
              <ModalFooter>
                <Button onClick={handleViewerClose}>Cerrar</Button>
              </ModalFooter>
            </Modal>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && selectedWorkflow && (
            <Modal show={true} onClose={() => setShowDeleteConfirm(false)}>
              <ModalHeader>
                <Text weight="bold" style={{ fontSize: '20px' }}>
                  Confirmar Desactivación
                </Text>
              </ModalHeader>
              <ModalContent>
                <Text>
                  ¿Estás seguro de que deseas desactivar el flujo de aprobación
                  "{selectedWorkflow.name}"?
                </Text>
                <Text style={{ marginTop: '12px', opacity: 0.7 }}>
                  Al desactivar el flujo, las nuevas solicitudes de trabajo se
                  publicarán automáticamente sin requerir aprobación. Las
                  solicitudes pendientes de aprobación no se verán afectadas.
                </Text>
              </ModalContent>
              <ModalFooter>
                <Flex gap="medium" justify="end">
                  <Button
                    kind="tertiary"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    color="negative"
                    onClick={confirmDeleteWorkflow}
                    data-testid="confirm-delete-workflow"
                  >
                    Desactivar Flujo
                  </Button>
                </Flex>
              </ModalFooter>
            </Modal>
          )}

          {/* Toast Notifications */}
          {toast && (
            <Toast type={toast.type as any} onClose={() => setToast(null)}>
              {toast.message}
            </Toast>
          )}
        </Layout>
      </PermissionGuard>
    </AuthGuard>
  )
}

export default WorkflowManagement
